import { useEffect } from 'react'
import { radius, theme } from '../theme'

/**
 * The one way out of a screen, always in the same place (8 Oct 2026).
 *
 * Sam: "across pages, I want the Home button consistently placed in the same
 * location". It had drifted: at the bottom of some screens (after the
 * scroll), top left on others, inside a pill on the 3D ones. Now it is
 * pinned to the top-left corner of the window, the same pixels on every
 * screen, and stays there while the page scrolls.
 *
 * The label says where it goes: "← Home" from a side room, "← Games" inside a
 * game, "Leave" in a session (which asks first), "Done" on the summary. One
 * button whose label changes, never two (the Shell rule of 18 Sep 2026).
 *
 * While it is on screen it pushes the page down a little (the --ws-top
 * variable that Page reads), so it never sits on top of a heading.
 */
let mounted = 0
const TOP = 'max(12px, env(safe-area-inset-top, 0px))'

export function ExitButton({
  onExit, label = '← Home',
}: { onExit: () => void; label?: string }) {
  useEffect(() => {
    mounted += 1
    document.documentElement.style.setProperty('--ws-top', '68px')
    return () => {
      mounted -= 1
      if (mounted <= 0) document.documentElement.style.removeProperty('--ws-top')
    }
  }, [])

  return (
    <button type="button" onClick={onExit} style={{
      position: 'fixed', top: TOP, left: 'max(12px, env(safe-area-inset-left, 0px))', zIndex: 55,
      // The 3D screens put it inside a bar that lets taps through to the
      // picture (pointer-events: none), and it inherited that: in the word
      // universe, Home did nothing (Sam, 18 Oct 2026).
      pointerEvents: 'auto',
      height: 42, padding: '0 16px', borderRadius: radius.pill, cursor: 'pointer',
      fontFamily: 'inherit', fontSize: 15, fontWeight: 800, color: theme.text2,
      background: theme.card, border: `1.5px solid ${theme.border}`,
      boxShadow: '0 2px 8px rgba(21,32,43,0.10)',
    }}>{label}</button>
  )
}
